import { useEffect, useState } from "react";
import Select from "react-select";
import { searchProjects } from "../../api/UserApi";
import { fromJS } from "immutable";
import useDebounce from "../../useDebounce";

type ProjectPickerProps = {
  onChange: (projects: any) => void;
  projects: any;
  error?: boolean;
  title?: string;
  placeholder?: string;
};

const ProjectPicker = (props: ProjectPickerProps) => {
  const { onChange, title, projects = fromJS([]), error = false, placeholder = "" } = props;
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [listOptions, setListOptions] = useState<any>([]);
  const [keywords, setKeywords] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>("");

  const convert2Opt = (project) => {
    const { data = {} } = project;
    const { avatarUrls = { "48x48": "" }, projectTypeKey = "" } = data;
    return {
      value: `${project.key}`,
      id: `${project.id}`,
      label: project.displayName,
      data: {
        avatarUrls,
        projectTypeKey,
      },
    };
  };

  const onInputChange = (val: string) => {
    setKeywords(val);
    setSearchError(""); // Clear previous errors
  };

  const performSearch = async () => {
    if (keywords.trim().length === 0) {
      setListOptions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setSearchError("");

    try {
      const items = await searchProjects(keywords);
      const options = items.map(convert2Opt);
      setListOptions(options);
    } catch (error) {
      console.error("Search failed:", error);
      setSearchError("Failed to search projects. Please try again.");
      setListOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search to avoid excessive API calls
  useDebounce(performSearch, [keywords], 300);

  useEffect(() => {
    setSelectedOptions(projects.toJS());
  }, [projects]);

  // Show error state: red border when error=true and no projects selected
  const hasNoProjects = !selectedOptions || selectedOptions.length === 0;
  const showErrorState = error && hasNoProjects;

  return (
    <>
      <div className={"py-2"}>{title}</div>
      <Select
        isMulti
        options={listOptions}
        value={selectedOptions}
        menuPlacement={"auto"}
        className="basic-multi-select"
        classNamePrefix={placeholder || `Select ${title}`}
        closeMenuOnSelect={false}
        onInputChange={onInputChange}
        isLoading={isLoading}
        placeholder={
          searchError || placeholder || `Search and select ${title?.toLowerCase() || "projects"}...`
        }
        noOptionsMessage={({ inputValue }) =>
          searchError
            ? searchError
            : inputValue.length === 0
              ? "Type to search projects..."
              : "No projects found"
        }
        onChange={(newProjects) => {
          setSelectedOptions(newProjects || []);
          onChange(newProjects);
        }}
        styles={{
          control: (base) =>
            showErrorState
              ? { ...base, borderColor: "#f87171", backgroundColor: "#fff1f2", boxShadow: "none" }
              : base,
        }}
      />
      {/* Show error message below when no projects are configured */}
      {showErrorState && (
        <p className="mt-1 text-xs text-red-500">
          You don&apos;t have any associated Jira projects
        </p>
      )}
    </>
  );
};

export default ProjectPicker;
